import { Extension } from '@tiptap/core';

export type FontSizeOptions = {
  types: string[];
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
  }
}

/**
 * Font size via the `textStyle` mark (requires `@tiptap/extension-text-style`,
 * which FormEditor already registers). Mirrors the `FontSize` extension shipped
 * in `@kula-ai/components` so both editors serialize to identical inline
 * `style="font-size: <n>px"` markup — the backend HTML sanitizer whitelists the
 * `font-size` CSS property, so it survives all the way to the candidate's inbox.
 */
export const FontSize = Extension.create<FontSizeOptions>({
  name: 'fontSize',

  addOptions() {
    return {
      types: ['textStyle'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) =>
              (element as HTMLElement).style.fontSize?.replace(/['"]+/g, '') ??
              null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) => {
          const extended = chain()
            .focus()
            .extendMarkRange('textStyle')
            .setMark('textStyle', { fontSize })
            .run();
          if (extended) {
            return true;
          }
          return chain().focus().setMark('textStyle', { fontSize }).run();
        },

      unsetFontSize:
        () =>
        ({ chain }) => {
          const cleared = chain()
            .focus()
            .extendMarkRange('textStyle')
            .setMark('textStyle', { fontSize: null })
            .removeEmptyTextStyle()
            .run();
          if (cleared) {
            return true;
          }
          return chain()
            .focus()
            .setMark('textStyle', { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});
